import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../components/Alert.js';
import { Field } from '../components/Field.js';
import { AuthLayout } from './AuthLayout.js';

export function LoginPage(): React.JSX.Element {
  const { status, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/dashboard'} replace />;
  }

  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(email, password);
      void navigate('/dashboard', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          No account yet? <Link to="/signup">Create one</Link>
        </>
      }
    >
      {error === null ? null : (
        <Alert tone="error">
          {error.message}
          {error.code === 'EMAIL_NOT_VERIFIED' ? (
            <>
              {' '}
              <Link to="/verify-email">Resend the confirmation email</Link>.
            </>
          ) : null}
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        noValidate
      >
        <Field
          label="Email address"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          error={error?.fieldError('email')}
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          error={error?.fieldError('password')}
        />

        <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="auth__links">
        <Link to="/forgot-password">Forgot your password?</Link>
      </div>
    </AuthLayout>
  );
}
